class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.aurhaInitTimer();
  }

  /* Aurha: cronômetro de reserva persistido em sessionStorage.
     Re-executado a cada render (o markup do timer volta novo). */
  aurhaInitTimer() {
    const el = this.querySelector('[data-aurha-timer]');
    if (!el) return;
    const valueEl = el.querySelector('[data-aurha-timer-value]');
    if (!valueEl) return;

    const minutes = parseInt(el.getAttribute('data-minutes'), 10) || 10;
    const durationMs = minutes * 60 * 1000;
    const KEY = 'aurhaCartReserveUntil';

    let until = parseInt(sessionStorage.getItem(KEY), 10);
    if (!until || isNaN(until) || until < Date.now()) {
      until = Date.now() + durationMs;
      sessionStorage.setItem(KEY, String(until));
    }

    if (this._aurhaTimerInterval) clearInterval(this._aurhaTimerInterval);
    const tick = () => {
      const remaining = Math.max(0, until - Date.now());
      const totalSec = Math.floor(remaining / 1000);
      const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
      const s = String(totalSec % 60).padStart(2, '0');
      const liveValue = this.querySelector('[data-aurha-timer-value]');
      if (liveValue) liveValue.textContent = `${m}:${s}`;
      if (remaining <= 0 && this._aurhaTimerInterval) clearInterval(this._aurhaTimerInterval);
    };
    tick();
    this._aurhaTimerInterval = setInterval(tick, 1000);
  }

  /* Aurha: dispara o confete se ligado no tema */
  aurhaConfettiIfEnabled() {
    if (this.getAttribute('data-aurha-confetti') === 'true' && typeof window.aurhaConfetti === 'function') {
      window.aurhaConfetti();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('aurha-cart-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('aurha-cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Aurha: adicionar item com o carrinho vazio (sugestões "Mais vendidos")
    // volta o markup preenchido, mas a classe is-empty fica presa no
    // <cart-drawer> — o CSS (component-cart-drawer.css) esconde o header e
    // comprime o inner, então os itens não aparecem até recarregar a página.
    // renderContents só roda após um /cart/add bem-sucedido, logo o carrinho
    // nunca está vazio aqui: limpamos is-empty do próprio elemento e dos filhos.
    this.classList.remove('is-empty');
    this.querySelectorAll('.is-empty').forEach((el) => el.classList.remove('is-empty'));
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.aurhaInitTimer();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Aurha: botão "Adicionar" das sugestões do carrinho.
   Reusa o mesmo pipeline do tema (/cart/add.js + renderContents). */
class AurhaUpsellAdd extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.variantId = this.getAttribute('data-variant-id');
    if (this.button) this.button.addEventListener('click', this.add.bind(this));
  }

  add() {
    const cart = document.querySelector('cart-drawer');
    if (!cart || !this.variantId) return;
    this.button.setAttribute('aria-busy', 'true');

    const sections = cart.getSectionsToRender().map((s) => s.id);
    const body = JSON.stringify({
      id: this.variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body,
    })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          this.button.removeAttribute('aria-busy');
          return;
        }
        cart.renderContents(parsed);
      })
      .catch(() => {
        this.button.removeAttribute('aria-busy');
      });
  }
}

customElements.define('upsell-add', AurhaUpsellAdd);

/* Aurha: clicar no - (menos) com quantidade 1 remove o item do carrinho
   (mesma ação da lixeira). Roda na fase de captura, antes do handler do tema. */
document.addEventListener(
  'click',
  function (event) {
    var minus = event.target.closest && event.target.closest('.cart-item button[name="minus"]');
    if (!minus) return;
    var item = minus.closest('.cart-item');
    if (!item) return;
    var input = item.querySelector('input.quantity__input');
    if (!input) return;
    if (parseInt(input.value, 10) <= 1) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var removeEl = item.querySelector('cart-remove-button');
      if (removeEl) removeEl.click();
    }
  },
  true
);
